import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Banner,
  Card,
  IndexFilters,
  Pagination,
  Select,
  useSetIndexFiltersMode,
} from "@shopify/polaris";
import { useSearchParams } from "react-router-dom";
import AppPageFrame from "../../components/layout/AppPageFrame";
import { searchSupportStores } from "../../services/supportApi";
import { isSupportAccessDenied } from "../../util/supportAccess";
import StoreTable from "./table";
import "./style.css";

const DEFAULT_SIZE = 25;
const DEBOUNCE_MS = 300;

export const STORE_HEALTH_VIEWS = [
  { label: "All", health: null, billing: null },
  { label: "Needs attention", health: "DEGRADED,CRITICAL", billing: null },
  { label: "Healthy", health: "HEALTHY", billing: null },
  { label: "Inactive", health: "INACTIVE", billing: null },
  { label: "Trial", health: null, billing: "TRIAL" },
];

export const STORE_SORT_OPTIONS = [
  { label: "Installed (newest)", value: "installedAt:desc", directionLabel: "Newest" },
  { label: "Installed (oldest)", value: "installedAt:asc", directionLabel: "Oldest" },
  { label: "Last activity (newest)", value: "lastActivityAt:desc", directionLabel: "Newest" },
  { label: "Name (A–Z)", value: "name:asc", directionLabel: "A–Z" },
  { label: "Name (Z–A)", value: "name:desc", directionLabel: "Z–A" },
  { label: "Oldest queued first", value: "oldestQueuedAt:asc", directionLabel: "Oldest" },
];

export const STORE_BILLING_OPTIONS = [
  { label: "All billing states", value: "" },
  { label: "Usage billing", value: "USAGE" },
  { label: "Trial", value: "TRIAL" },
  { label: "No active subscription", value: "NO_ACTIVE_SUBSCRIPTION" },
  { label: "Billing attention", value: "ATTENTION" },
  { label: "Legacy fixed (diagnostic)", value: "LEGACY_FIXED" },
];

const positiveInteger = (value, fallback) => {
  const parsed = Number.parseInt(value, 10);
  return Number.isInteger(parsed) && parsed > 0 ? parsed : fallback;
};

const Store = () => {
  const { mode, setMode } = useSetIndexFiltersMode();
  const [searchParams, setSearchParams] = useSearchParams();
  const [queryValue, setQueryValue] = useState(searchParams.get("query") || "");
  const [debouncedQuery, setDebouncedQuery] = useState(queryValue);
  const requestSequence = useRef(0);
  const [result, setResult] = useState({
    content: [],
    totalElements: 0,
    totalPages: 0,
    page: 0,
    facets: {},
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const page = Math.max(0, positiveInteger(searchParams.get("page"), 1) - 1);
  const size = positiveInteger(searchParams.get("size"), DEFAULT_SIZE);
  const health = searchParams.get("health") || "";
  const billing = searchParams.get("billing") || "";
  const country = searchParams.get("country") || "";
  const sort = searchParams.get("sort") || "installedAt";
  const direction = searchParams.get("direction") || "desc";
  const sortSelected = [`${sort}:${direction}`];

  const selectedView = Math.max(
    0,
    STORE_HEALTH_VIEWS.findIndex(
      (view) => (view.health || "") === health && (view.billing || "") === billing
    )
  );

  const updateParams = useCallback(
    (changes, { resetPage = true } = {}) => {
      const next = new URLSearchParams(searchParams);
      Object.entries(changes).forEach(([key, value]) => {
        if (value === null || value === undefined || value === "") next.delete(key);
        else next.set(key, String(value));
      });
      if (resetPage) next.delete("page");
      setSearchParams(next, { replace: true });
    },
    [searchParams, setSearchParams]
  );

  useEffect(() => {
    const urlQuery = searchParams.get("query") || "";
    if (urlQuery !== queryValue) setQueryValue(urlQuery);
  }, [queryValue, searchParams]);

  useEffect(() => {
    const timer = window.setTimeout(() => setDebouncedQuery(queryValue.trim()), DEBOUNCE_MS);
    return () => window.clearTimeout(timer);
  }, [queryValue]);

  const requestBody = useMemo(
    () => ({
      page,
      size,
      query: debouncedQuery || null,
      health: health ? health.split(",").filter(Boolean) : [],
      billing: billing ? [billing] : [],
      country: country ? [country] : [],
      sort,
      direction,
    }),
    [billing, country, debouncedQuery, direction, health, page, size, sort]
  );

  const loadStores = useCallback(() => {
    const controller = new AbortController();
    const requestId = ++requestSequence.current;
    setLoading(true);
    setError(null);

    searchSupportStores(requestBody, { signal: controller.signal })
      .then((payload) => {
        if (requestId !== requestSequence.current) return;
        setResult({
          content: Array.isArray(payload?.content) ? payload.content : [],
          totalElements: Number(payload?.totalElements) || 0,
          totalPages: Number(payload?.totalPages) || 0,
          page: Number.isInteger(Number(payload?.page)) ? Number(payload.page) : page,
          facets: payload?.facets && typeof payload.facets === "object" ? payload.facets : {},
        });
        setLoading(false);
      })
      .catch((requestError) => {
        if (requestError?.name === "AbortError" || requestId !== requestSequence.current) return;
        setError(requestError?.message || "The store directory is unavailable.");
        setLoading(false);
      });

    return () => controller.abort();
  }, [page, requestBody]);

  useEffect(() => loadStores(), [loadStores]);

  const facetKeys = ["all", "attention", "healthy", "inactive", "trial"];
  const tabs = STORE_HEALTH_VIEWS.map((view, index) => {
    const count = Number(result.facets?.[facetKeys[index]]);
    return {
      id: `store-health-${index}`,
      content: Number.isFinite(count) ? `${view.label} (${count.toLocaleString("en-US")})` : view.label,
      index,
    };
  });
  const displaySizes = [10, 25, 50, 100].map((value) => ({
    label: String(value),
    value: String(value),
  }));
  const countryOptions = [
    { label: "All countries", value: "" },
    ...Object.entries(result.facets?.countries || {})
      .filter(([label, count]) => label && Number.isFinite(Number(count)))
      .sort(([left], [right]) => left.localeCompare(right))
      .map(([label, count]) => ({
        label: `${label} (${Number(count).toLocaleString("en-US")})`,
        value: label,
      })),
  ];
  if (country && !countryOptions.some((option) => option.value === country)) {
    countryOptions.splice(1, 0, { label: country, value: country });
  }
  const filters = [
    {
      key: "country",
      label: "Country",
      filter: (
        <Select
          label="Country"
          labelHidden
          options={countryOptions}
          value={country}
          onChange={(value) => updateParams({ country: value })}
        />
      ),
      shortcut: true,
    },
    {
      key: "billing",
      label: "Billing",
      filter: (
        <Select
          label="Billing"
          labelHidden
          options={STORE_BILLING_OPTIONS}
          value={billing}
          onChange={(value) => updateParams({ billing: value, health: null })}
        />
      ),
      shortcut: true,
    },
    {
      key: "size",
      label: "Rows per page",
      filter: (
        <Select
          label="Rows per page"
          labelHidden
          options={displaySizes}
          value={String(size)}
          onChange={(value) => updateParams({ size: value })}
        />
      ),
    },
  ];

  const appliedFilters = [];
  if (country) {
    appliedFilters.push({
      key: "country",
      label: `Country: ${country}`,
      onRemove: () => updateParams({ country: null }),
    });
  }
  if (billing && selectedView === 0) {
    appliedFilters.push({
      key: "billing",
      label: `Billing: ${billing}`,
      onRemove: () => updateParams({ billing: null }),
    });
  }

  const clearAll = () => {
    setQueryValue("");
    setDebouncedQuery("");
    setSearchParams(new URLSearchParams(), { replace: true });
  };

  const totalPages = result.totalPages;

  return (
    <AppPageFrame title="Stores" maxWidth="1600px" gap="16px">
      <div className="support-directory-intro">
        <div>
          <h2>Support store directory</h2>
          <p>Find a merchant by identity, health, connection readiness, or usage billing state.</p>
        </div>
        <strong>{result.totalElements.toLocaleString("en-US")} stores</strong>
      </div>

      {error ? (
        <Banner
          title={isSupportAccessDenied(error) ? "Support access is required" : "Store directory could not be refreshed"}
          status="critical"
          action={{ content: "Retry", onAction: loadStores }}
        >
          <p>{error}{result.content.length ? " Showing the last successful result." : ""}</p>
        </Banner>
      ) : null}

      <Card>
        <IndexFilters
          sortOptions={STORE_SORT_OPTIONS}
          sortSelected={sortSelected}
          onSort={(selection) => {
            const [nextSort, nextDirection] = String(selection?.[0] || "installedAt:desc").split(":");
            updateParams({ sort: nextSort, direction: nextDirection });
          }}
          queryValue={queryValue}
          queryPlaceholder="Search store name, domain, email, owner, or Shopify ID"
          onQueryChange={(value) => {
            setQueryValue(value);
            updateParams({ query: value || null });
          }}
          onQueryClear={() => {
            setQueryValue("");
            updateParams({ query: null });
          }}
          tabs={tabs}
          selected={selectedView}
          onSelect={(index) => {
            const view = STORE_HEALTH_VIEWS[index] || STORE_HEALTH_VIEWS[0];
            updateParams({ health: view.health, billing: view.billing });
          }}
          filters={filters}
          appliedFilters={appliedFilters}
          onClearAll={clearAll}
          mode={mode}
          setMode={setMode}
          loading={loading}
        />
        <StoreTable loading={loading && result.content.length === 0} stores={result.content} />
        {totalPages > 1 ? (
          <div className="support-directory-pagination">
            <span>
              Page {page + 1} of {totalPages}
            </span>
            <Pagination
              hasPrevious={page > 0}
              onPrevious={() => updateParams({ page }, { resetPage: false })}
              hasNext={page + 1 < totalPages}
              onNext={() => updateParams({ page: page + 2 }, { resetPage: false })}
            />
          </div>
        ) : null}
      </Card>
    </AppPageFrame>
  );
};

export default Store;
